import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Header from "../Header/Header";
import Footer from "../Footer/Footer";
import { toast } from "react-toastify";
import { getUserProfile, UserProfile } from "../../API/UserProfileApi/GetUserProfile";
import { viewAddedAddressByIdAsync } from "../../API/Address/ViewAddedAddressByIdAsync";
import { Address } from "../../API/Address/ViewAllAddedAddressByUserAsync";
import { updateLocalStorageAddressData } from "../../utils/updateLocalStorageAddressData";
import { useLocalization } from "../Context/MultiLanguageContext";
import MapEditLocation from "../Maps/MapEditLocation";
import { updateAddressAsync } from "../../API/Address/UpdateAddressAsync";
import { confirmUpdateLocationWithPamCodeChangeAsync } from "../../API/Address/ConfirmUpdateLocation";
import { addressPrivacy } from "../../API/Address/AddressPrivacy"
import { getNearbyPinCodesPamCodeAsync } from '../../API/Address/GetNearbyPinCodesPamCodeAsync';
import { addAddressImagesAsync } from "../../API/Address/AddAddressImagesAsync";
import { deleteAddressImageAsync } from "../../API/Address/DeleteAddressImageAsync";
import { getAddressImageAsync } from "../../API/Address/GetAddressImageAsync";
import { Eye, Image as ImageIcon, Link2, StickyNote, Trash2, X } from "lucide-react";

const EditAddress: React.FC<{ updateUserName: (newUserName: string) => void }> = ({ updateUserName }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLocalization();

  const [stateAddress, setStateAddress] = useState<any | null>(null);

  const [formData, setFormData] = useState({
    addressLine1: "",
    addressLine2: "",
    addressLine3: "",
    pamCode: "",
    addressType: "",
    pinCode: "",
    blockCode: "",
    notes: "",
    url: "",
  });

  interface PinCodeData {
    pinCode: string;
    blockCode: string;
    pamCode: string;
  }

  interface Errors {
    addressLine1: string;
    addressLine2: string;
    addressLine3: string;
    pinCode: string;
    addressType: string;
    location: string;
  }

  const [errors, setErrors] = useState<Errors>({
    addressLine1: "",
    addressLine2: "",
    addressLine3: "",
    pinCode: "",
    addressType: "",
    location: "",
  });
  const [selectedAddressType, setSelectedAddressType] = useState("home");
  const [customAddressType, setCustomAddressType] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [userName, setUserName] = useState("");
  const [addressDetails, setAddressDetails] = useState<Address | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [address, setAddress] = useState<any | null>(null);
  const [isLocationChanged, setIsLocationChanged] = useState(false);

  const [initialCoordinates, setInitialCoordinates] = useState<{ latitude: number; longitude: number } | null>(null);
  const [isAddressChanged, setIsAddressChanged] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [APIresponse, setAPIresponse] = useState<{ message: string; isPamCodeChanged: boolean; predictedPamCode: string } | null>(null);
  const [nearbyPinCodes, setNearbyPinCodes] = useState<PinCodeData[]>([]);
  const [newPinCode, setNewPinCode] = useState<string>("");
  const [isLessthanFifteenDays, setIsLessthanFifteenDays] = useState<boolean>(false);

  const [initialPinBlock, setInitialPinBlock] = useState<string>("");
  type AddressImage = { id: number; url: string };
  const [addressImages, setAddressImages] = useState<AddressImage[]>([]);
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<number | null>(null);
  const [isDeletingImage, setIsDeletingImage] = useState(false);
  const [selectedImage, setSelectedImage] = useState<AddressImage | null>(null);
  const maxAddressImages = 3;

  const today = new Date();
  const createdDate = stateAddress?.createdDate;

  useEffect(() => {
    setIsLessthanFifteenDays(
      createdDate ? new Date(createdDate) > new Date(today.setDate(today.getDate() - 15)) : false
    );

  }, [createdDate, today]);

  const handleLocationChange = (id: string, latitude: number, longitude: number) => {
    setAddress((prevAddress: Address | null) =>
      prevAddress && prevAddress.id === id
        ? { ...prevAddress, latitude, longitude }
        : prevAddress
    );

    if (!initialCoordinates) {
      setInitialCoordinates({ latitude, longitude });
    }

    const hasLocationChanged =
      initialCoordinates?.latitude !== latitude ||
      initialCoordinates?.longitude !== longitude;

    setIsLocationChanged(hasLocationChanged);

    fetchNearbyPinCodes(latitude, longitude);
  };

  useEffect(() => {
    if (address && address.latitude && address.longitude) {
      fetchNearbyPinCodes(address.latitude, address.longitude);
    }
  }, [address]);

  const handleUpdate = async () => {
    setIsSubmitting(true);
    const token = localStorage.getItem("token");
    if (!token) {
      setErrorMessage(t("sessionExpired"));
      navigate("/");
      return;
    }

    try {
      if (!address?.latitude || !address?.longitude) {
        setErrorMessage(t("locationNotSelected"));
        setIsSubmitting(false);
        return;
      }

      const locationResult = await updateAddressAsync(
        id as string,
        {
          addressLine1: formData.addressLine1,
          addressLine2: formData.addressLine2,
          addressLine3: formData.addressLine3,
          addressType: formData.addressType,
          latitude: String(address.latitude),
          longitude: String(address.longitude),
          pamCode: (formData.pinCode || "") + (formData.blockCode || ""),
          notes: formData.notes,
          url: formData.url,
        },
        token,
        t
      );

      if (locationResult !== null) {
        updateLocalStorageAddressData(Number(id), {
          latitude: Number(address.latitude),
          longitude: Number(address.longitude),
        });

        setAPIresponse(locationResult);
        setIsModalVisible(true);


        if (locationResult.isPamCodeChanged === false) {
          setTimeout(() => {
            navigate(`/address-details/${id}`, { state: { address: { ...addressDetails, ...formData } } });
          }, 1000);
        }
      }

    } catch (error: any) {
      // console.error(error);
      setErrorMessage(`${t("errorUpdating")}: ${error.message}`);
    } finally {
      setIsSubmitting(false);
      setIsAddressChanged(false);
      setIsLocationChanged(false);
    }
  };

  const handleConfirmUpdate = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error(t("sessionExpired"));
      navigate("/");
      return;
    }

    try {
      const formattedPamCode = APIresponse?.predictedPamCode.replace(/-/g, "");
      const payload = {
        addressLine1: formData.addressLine1,
        addressLine2: formData.addressLine2,
        addressLine3: formData.addressLine3,
        addressType: formData.addressType,
        latitude: String(address.latitude),
        longitude: String(address.longitude),
        pinCode: formattedPamCode?.slice(0, 6) || "",
        blockCode: formattedPamCode?.slice(6, 10) || "",
        addressCode: formattedPamCode?.slice(10) || "",
        notes: formData.notes,
        url: formData.url,
      };
      await confirmUpdateLocationWithPamCodeChangeAsync(id as string, payload, token, t);


      setTimeout(() => {
        navigate(`/address-details/${id}`, { state: { address: { ...addressDetails, ...formData } } });
      }, 1000);

    } catch (error) {
      // console.error("Error confirming location update:", error);
      toast.error(t("errorConfirmingLocationUpdate"));
    } finally {
      setIsModalVisible(false);
    }
  };

  const setViewAll = async () => {

    const token = localStorage.getItem("token");
    if (!token) {
      // console.error("Token not found in localStorage");
      setErrorMessage(t("sessionExpired"));
      navigate("/");
      return;
    }

    try {
      const addressFromId =
        await viewAddedAddressByIdAsync(id || "", token, t);
      setAddress(addressFromId?.[0] ?? null);


    }
    catch (error: any) {
      // console.error(error);
      setErrorMessage(
        `${t("errorUpdating")}: ${error.message}`
      );
    }
  }



  const togglePrivacy = async () => {
    setAddress((prev: any) =>
      prev ? { ...prev, isPrivate: !prev.isPrivate } : prev
    );

    const token = localStorage.getItem("token");
    if (!token) {
      setErrorMessage(t("sessionExpired"));
      navigate("/");
      return;
    }

    try {
      await addressPrivacy(Number(id), token, t);
    } catch (error: any) {
      setErrorMessage(`${t("errorUpdating")}: ${error.message}`);

      setAddress((prev: any) =>
        prev ? { ...prev, isPrivate: !prev.isPrivate } : prev
      );
    } finally {
      await setViewAll();
    }
  };

  const fetchNearbyPinCodes = async (latitude: number, longitude: number) => {
    const token = localStorage.getItem('token');
    if (!token) {
      // console.error('Token not found in localStorage');
      toast.error(t("sessionExpired"));
      return;
    }

    try {
      const formattedPinCodes = await getNearbyPinCodesPamCodeAsync(
        latitude,
        longitude,
        token, t
      );
      if (!formattedPinCodes) {
        return;
      }
      setNearbyPinCodes(formattedPinCodes);
      // Try to select the initial pin+block if present in the dropdown
      if (initialPinBlock) {
        const found = formattedPinCodes.find(pin => (pin.pinCode + pin.blockCode) === initialPinBlock);
        if (found) {
          setFormData((prevData) => ({
            ...prevData,
            pinCode: found.pinCode,
            blockCode: found.blockCode,
            pamCode: found.pamCode
          }));
          return;
        }
      }
      if (formattedPinCodes.length > 0) {
        setFormData((prevData) => ({
          ...prevData,
          pinCode: formattedPinCodes[0].pinCode,
          blockCode: formattedPinCodes[0].blockCode,
          pamCode: formattedPinCodes[0].pamCode
        }));
      }
    } catch (error) {
      // console.error('Error fetching pin codes:', error);
    }
  };


  useEffect(() => {
    const fetchAddressDetails = async () => {
      const token = localStorage.getItem("token") || "";
      if (!id) return;

      try {
        const addressFromId = await viewAddedAddressByIdAsync(id || "", token, t);
        setStateAddress(addressFromId?.[0] ?? null);
        if (addressFromId?.[0]) {
          setInitialPinBlock((addressFromId[0].pinCode || "") + (addressFromId[0].blockCode || ""));
        }
      } catch (error) {
        setStateAddress(null);
      }
    };

    fetchAddressDetails();
  }, []);


  useEffect(() => {
    if (stateAddress) {

      setAddressDetails(stateAddress);
      setAddress(stateAddress);  // Set the address for the map

      const loadedAddressType = stateAddress.addressType || "";
      if (loadedAddressType && !["home", "office"].includes(loadedAddressType.toLowerCase())) {
        setSelectedAddressType("custom");
        setCustomAddressType(loadedAddressType);
      } else {
        setSelectedAddressType(loadedAddressType.toLowerCase());
      }

      setFormData({
        addressLine1: stateAddress.addressLine1 || "",
        addressLine2: stateAddress.addressLine2 || "",
        addressLine3: stateAddress.addressLine3 || "",
        pamCode: stateAddress.pamCode || "",
        addressType: loadedAddressType || "",
        pinCode: stateAddress.pinCode || "",
        blockCode: stateAddress.blockCode || "",
        notes: stateAddress.notes || "",
        url: stateAddress.url || "",
      });
    }
  }, [stateAddress]);

  useEffect(() => {
    const loadAddressImages = async () => {
      if (!stateAddress?.images?.length) {
        setAddressImages([]);
        return;
      }

      setIsLoadingImages(true);
      try {
        const token = localStorage.getItem("token") || "";
        const loadedImages: AddressImage[] = [];
        for (const image of stateAddress.images) {
          try {
            const imageBlob = await getAddressImageAsync(Number(stateAddress.id), Number(image.id), token, t);
            if (imageBlob) loadedImages.push({ id: Number(image.id), url: URL.createObjectURL(imageBlob) });
          } catch (error) {
            console.error(`Failed to load image ${image.id}:`, error);
          }
        }
        setAddressImages(loadedImages);
      } finally {
        setIsLoadingImages(false);
      }
    };

    loadAddressImages();
  }, [stateAddress, t]);

  useEffect(() => () => {
    addressImages.forEach((image) => URL.revokeObjectURL(image.url));
  }, [addressImages]);

  const handleAddImages = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length || !address?.id) return;

    const remainingSlots = maxAddressImages - addressImages.length;
    if (files.length > remainingSlots) {
      toast.error(`You can add only ${remainingSlots} more image${remainingSlots === 1 ? "" : "s"}.`);
      return;
    }

    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    const invalidFile = files.find((file) => !allowedTypes.includes(file.type) || file.size > 5 * 1024 * 1024);
    if (invalidFile) {
      toast.error(!allowedTypes.includes(invalidFile.type) ? "Only JPG, PNG and WEBP images are allowed." : "Each image must be less than 5 MB.");
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      toast.error(t("sessionExpired"));
      navigate("/");
      return;
    }

    try {
      setIsUploadingImages(true);
      await addAddressImagesAsync(Number(address.id), files, token, t);
      toast.success("Images added successfully.");
      const refreshedAddress = await viewAddedAddressByIdAsync(id || "", token, t);
      setStateAddress(refreshedAddress?.[0] ?? null);
      setAddress(refreshedAddress?.[0] ?? null);
    } catch (error) {
      console.error("Error adding address images:", error);
      toast.error(t("somethingWentWrong"));
    } finally {
      setIsUploadingImages(false);
    }
  };

  const handleDeleteImage = async (imageId: number) => {
    if (!address?.id) return;
    const token = localStorage.getItem("token") || "";
    try {
      setIsDeletingImage(true);
      await deleteAddressImageAsync(Number(address.id), imageId, token, t);
      setAddressImages((images) => images.filter((image) => image.id !== imageId));
      setImageToDelete(null);
      toast.success("Image deleted successfully.");
    } catch (error) {
      console.error("Error deleting address image:", error);
      toast.error(t("somethingWentWrong"));
    } finally {
      setIsDeletingImage(false);
    }
  };


  useEffect(() => {
    if (addressDetails) {
      const hasAddressChanged =
        formData.addressLine1 !== addressDetails.addressLine1 ||
        formData.addressLine2 !== addressDetails.addressLine2 ||
        formData.addressLine3 !== addressDetails.addressLine3 ||
        formData.addressType !== addressDetails.addressType ||
        formData.notes !== (addressDetails.notes || "") ||
        formData.url !== (addressDetails.url || "");

      setIsAddressChanged(hasAddressChanged);
    }
  }, [formData, addressDetails]);



  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleAddressTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedAddressType(value);
    if (value === "custom") {

      setCustomAddressType("");
      setFormData((prevData) => ({
        ...prevData,
        addressType: "",
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        addressType: value,
      }));
    }
  };

  const handleCustomAddressTypeChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCustomAddressType(e.target.value);
    setFormData((prevData) => ({
      ...prevData,
      addressType: e.target.value,
    }));
  };


  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  useEffect(() => {
    const storedUserName = localStorage.getItem('userName');
    if (storedUserName) {
      setUserName(storedUserName);
    }
  }, []);

  const fetchAndHandleUserProfile = async () => {
    try {
      const data = await getUserProfile(t);
      setUserProfile(data);
      setUserName(data.userName);
      updateUserName(data.userName);
      navigate('/profile', {
        state: {
          userName: data.userName,
          email: data.email,
          phoneNumber: data.phoneNumber,
        },
      });
    } catch (error) {
      toast.error(t("sessionExpired"));
      navigate('/home');
    }
  };


  const handleLogout = () => {
    localStorage.removeItem("userToken");
    localStorage.removeItem('authToken');
    localStorage.removeItem('token');
    localStorage.removeItem('userName');

    sessionStorage.clear();
    navigate("/", { replace: true });
  };

  const EditAddressSkeleton = () => (
    <div className="w-full p-4 sm:p-8 bg-white rounded-lg shadow-md animate-pulse">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <div className="h-4 w-32 bg-gray-200 rounded mb-2"></div>
          <div className="h-10 w-full bg-gray-200 rounded"></div>
        </div>
        <div>
          <div className="h-4 w-20 bg-gray-200 rounded mb-2"></div>
          <div className="h-10 w-full bg-gray-200 rounded"></div>
        </div>
        <div>
          <div className="h-4 w-16 bg-gray-200 rounded mb-2"></div>
          <div className="h-10 w-full bg-gray-200 rounded"></div>
        </div>
        <div>
          <div className="h-4 w-24 bg-gray-200 rounded mb-2"></div>
          <div className="h-10 w-full bg-gray-200 rounded"></div>
        </div>
        <div className="col-span-1">
          <div className="h-4 w-40 bg-gray-200 rounded mb-2"></div>
          <div className="h-10 w-full bg-gray-200 rounded"></div>
        </div>
        <div className="col-span-1 lg:col-span-4 flex items-center justify-end gap-4 mt-4">
          <div className="h-10 w-24 bg-gray-200 rounded"></div>
          <div className="h-10 w-24 bg-gray-200 rounded"></div>
        </div>
      </div>
    </div>
  );


  const handlePinCodeChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedPamCode = e.target.value;
    setNewPinCode(selectedPamCode);

    const selectedPin = nearbyPinCodes.find(pin => pin.pamCode === selectedPamCode);
    if (selectedPin) {
      setFormData(prev => ({
        ...prev,
        pinCode: selectedPin.pinCode,
        blockCode: selectedPin.blockCode,
        pamCode: selectedPin.pamCode
      }));
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-100">
      <Header userName={userName} fetchUserProfile={fetchAndHandleUserProfile} handleLogout={handleLogout} />

      <main className="p-4 sm:p-6 flex-grow">
        <h1 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">
          {t("editAddress")}
        </h1>

        {loading ? (
          <EditAddressSkeleton />
        ) : (
          <div className="w-full p-4 sm:p-8 bg-white rounded-lg shadow-md">
            {errorMessage && (
              <p className="text-red-500 mb-4">{errorMessage}</p>
            )}

            {isLessthanFifteenDays && <div className="relative h-[200px] sm:h-[180px] md:h-[250px]">
              {address ? (
                <MapEditLocation addresses={[address]} onLocationChange={handleLocationChange} />
              ) : (
                <p className="text-center text-gray-500">{t("loadingMap")}</p>
              )}
            </div>}

            {loading ? (
              <p className="text-center text-gray-600">{t("loading")}</p>
            ) : (
              <form className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                <div>
                  <label htmlFor="addressLine1" className="block text-sm font-medium text-gray-700 mb-2">
                    {t("buildingNumber")}
                  </label>
                  <input
                    type="text"
                    id="addressLine1"
                    name="addressLine1"
                    value={formData.addressLine1}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 rounded-lg bg-slate-50 text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="addressLine2" className="block text-sm font-medium text-gray-700 mb-2">
                    {t("street")}
                  </label>
                  <input
                    type="text"
                    id="addressLine2"
                    name="addressLine2"
                    value={formData.addressLine2}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 rounded-lg bg-slate-50  text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="addressLine3" className="block text-sm font-medium text-gray-700 mb-2">
                    {t("city")}
                  </label>
                  <input
                    type="text"
                    id="addressLine3"
                    name="addressLine3"
                    value={formData.addressLine3}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 rounded-lg bg-slate-50 text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="pamCode" className="block text-sm font-medium text-gray-700 mb-2">
                    {t("pamCode")}
                  </label>
                  <input
                    type="text"
                    id="pamCode"
                    name="pamCode"
                    value={address?.pamCode}
                    readOnly
                    className="w-full px-4 py-2 rounded-lg bg-slate-50 text-black"
                  />
                </div>
                <div className="col-span-1">
                  <label htmlFor="addressType" className="block text-sm font-medium text-gray-700 mb-2">
                    {t("addressType")}
                  </label>
                  <select
                    id="addressType"
                    name="addressType"
                    value={selectedAddressType}
                    onChange={handleAddressTypeChange}
                    className="w-full rounded-lg bg-slate-50 py-3 pl-4 pr-10 outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="home">{t("home")}</option>
                    <option value="office">{t("office")}</option>
                    <option value="custom">{t("other")}</option>
                  </select>
                  {selectedAddressType === "custom" && (
                    <input
                      type="text"
                      value={customAddressType}
                      onChange={handleCustomAddressTypeChange}
                      placeholder={t("enterAddressType")}
                      className="mt-2 w-full rounded-lg bg-slate-50 py-2 px-4 outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  )}
                </div>

                <div>
                  <label htmlFor="pinCode" className="block text-sm font-medium text-gray-700">
                    {t("postalCode")}
                  </label>
                  <select
                    id="pinCodeDropdown"
                    name="pinCodeDropdown"
                    value={formData.pamCode}
                    onChange={handlePinCodeChangeSelect}
                    className={`mt-2 w-full rounded-lg bg-slate-50 py-2 px-4 outline-none focus:ring-2 focus:ring-blue-500 
                      ${errors?.pinCode ? 'border-red-500' : ''}`}
                  >
                    {nearbyPinCodes?.length > 0 && (
                      <option value={nearbyPinCodes[0].pamCode}>
                        {nearbyPinCodes[0].pamCode.slice(0, 6)} (Recommended)
                      </option>
                    )}
                    {nearbyPinCodes?.slice(1).map((pin, index) => (
                      <option key={index + 1} value={pin.pamCode}>
                        {pin.pamCode.slice(0, 6)}
                      </option>
                    ))}
                  </select>
                  {errors?.pinCode && (
                    <p className="mt-1 text-sm text-red-500">{errors.pinCode}</p>
                  )}
                </div>

                <section className="col-span-full mt-2 rounded-xl border border-slate-200 bg-slate-50/70 p-4 sm:p-5" aria-labelledby="additional-information-heading">

                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    <div>
                      <label htmlFor="url" className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700">
                        <Link2 className="h-4 w-4 text-blue-600" aria-hidden="true" />
                        URL <span className="font-normal text-slate-400">(Optional)</span>
                      </label>
                      <input
                        type="url"
                        id="url"
                        name="url"
                        value={formData.url}
                        onChange={handleInputChange}
                        placeholder="https://example.com"
                        maxLength={200}
                        className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="mt-1.5 text-right text-xs text-slate-400">
                        {formData.url.length}/200
                      </p>
                    </div>

                    <div>
                      <label htmlFor="notes" className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700">
                        <StickyNote className="h-4 w-4 text-blue-600" aria-hidden="true" />
                        Notes <span className="font-normal text-slate-400">(Optional)</span>
                      </label>
                      <textarea
                        id="notes"
                        name="notes"
                        value={formData.notes}
                        onChange={handleInputChange}
                        placeholder="Add any useful details about this address"
                        maxLength={200}
                        rows={3}
                        className="w-full resize-none rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="mt-1.5 text-right text-xs text-slate-400">
                        {formData.notes.length}/200
                      </p>
                    </div>
                  </div>
                </section>

                <section
                  className="col-span-full w-full rounded-xl border border-slate-200 bg-slate-50 p-4 sm:p-5"
                  aria-labelledby="address-images-heading"
                >
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <ImageIcon className="h-5 w-5 text-blue-600" aria-hidden="true" />
                      <h2 id="address-images-heading" className="text-base font-semibold text-slate-900">Address images</h2>
                      {!isLoadingImages && <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700">{addressImages.length}/{maxAddressImages}</span>}
                    </div>
                  </div>

                  {isLoadingImages ? (
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 max-w-3xl">
                      {[1, 2, 3].map((item) => <div key={item} className="h-32 animate-pulse rounded-lg bg-slate-200" />)}
                    </div>
                  ) : addressImages.length === 0 ? (
                    <label htmlFor="address-image-upload" className={`flex h-32 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-white transition-all hover:border-blue-500 hover:bg-blue-50 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500 ${isUploadingImages ? "pointer-events-none opacity-60" : ""}`}>
                      <span className="mb-2 flex h-11 w-11 items-center justify-center rounded-full bg-blue-50 text-2xl text-blue-500" aria-hidden="true">+</span>
                      <span className="text-sm font-medium text-gray-600">{isUploadingImages ? "Adding..." : "Add image"}</span>
                      <span className="mt-1 text-xs text-gray-400">{maxAddressImages} images remaining</span>
                      <input id="address-image-upload" type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={handleAddImages} disabled={isUploadingImages} className="sr-only" />
                    </label>
                  ) : (
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 max-w-3xl">
                      {addressImages.map((image, index) => (
                        <div
                          key={image.id}
                          className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm"
                        >
                          <div className="relative h-40 bg-slate-100">
                            <button
                              type="button"
                              onClick={() => setSelectedImage(image)}
                              className="h-full w-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                              aria-label={`View address image ${index + 1} maximized`}
                            >
                              <img src={image.url} alt={`Address image ${index + 1}`} className="h-full w-full object-cover" />
                            </button>
                            <div className="absolute right-2 top-2 flex gap-1">
                              <button
                                type="button"
                                onClick={() => setSelectedImage(image)}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-black/60 text-white hover:bg-black/80 focus:outline-none focus:ring-2 focus:ring-white"
                                title="View image"
                                aria-label={`View address image ${index + 1}`}
                              >
                                <Eye className="h-4 w-4" aria-hidden="true" />
                              </button>
                              <button
                                type="button"
                                onClick={() => setImageToDelete(image.id)}
                                disabled={isDeletingImage || isUploadingImages}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-black/60 text-white hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-white disabled:opacity-50"
                                title="Delete image"
                                aria-label={`Delete address image ${index + 1}`}
                              >
                                <Trash2 className="h-4 w-4" aria-hidden="true" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                      {addressImages.length < maxAddressImages && (
                        <label htmlFor="address-image-upload" className={`flex h-40 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-white transition-all hover:border-blue-500 hover:bg-blue-50 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500 ${isUploadingImages ? "pointer-events-none opacity-60" : ""}`}>
                          <span className="mb-2 flex h-11 w-11 items-center justify-center rounded-full bg-blue-50 text-2xl text-blue-500" aria-hidden="true">+</span>
                          <span className="text-sm font-medium text-gray-600">{isUploadingImages ? "Adding..." : "Add image"}</span>
                          <span className="mt-1 text-xs text-gray-400">{maxAddressImages - addressImages.length} image{maxAddressImages - addressImages.length === 1 ? "" : "s"} remaining</span>
                          <input id="address-image-upload" type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={handleAddImages} disabled={isUploadingImages} className="sr-only" />
                        </label>
                      )}
                    </div>
                  )}
                </section>

                <div className="col-span-full lg:flex items-center gap-4 mt-2 justify-between">

                  <div className="flex items-center space-x-3">
                    <label htmlFor="privacySwitch" className="text-sm font-medium text-gray-700">Address Privacy : </label>
                    <span
                      className="text-sm font-medium text-gray-700 cursor-default"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {address?.isPrivate ? t("private") : t("public")}
                    </span>
                    <input
                      type="checkbox"
                      id="privacySwitch"
                      checked={address?.isPrivate}
                      onChange={togglePrivacy}
                      className="cursor-pointer relative w-11 h-6 rounded-full appearance-none bg-gray-300 checked:bg-blue-600 transition-colors duration-200 outline-none
                      before:content-[''] before:absolute before:w-5 before:h-5 before:rounded-full before:bg-white before:shadow before:top-0.5 before:left-0.5
                      checked:before:translate-x-5 before:transition-transform before:duration-200"
                    />
                  </div>

                  <div className="justify-between space-x-3 mt-4">
                    <button
                      type="button"
                      onClick={handleUpdate}
                      disabled={isSubmitting}
                      className={`py-2 px-4 text-white rounded-md shadow focus:outline-none focus:ring-2 focus:ring-blue-500
                         ${isSubmitting
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-blue-600 hover:bg-blue-700"
                        }`}
                    >
                      {isSubmitting ? t("updating") : t("update")}
                    </button>


                    <button
                      onClick={() => navigate("/addresslist")}
                      className="py-2 px-4 bg-gray-500 text-white rounded-md shadow hover:bg-gray-600 focus:outline-none"
                      disabled={isSubmitting}
                    >
                      {t("cancel")}
                    </button>

                  </div>
                </div>
              </form>
            )}
          </div>
        )}
      </main>

      {isModalVisible && APIresponse?.predictedPamCode && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-[1000]">
          <div className="bg-white p-6 rounded-lg shadow-lg w-11/12 md:w-1/3">
            <h4 className="text-xl font-semibold mb-4 text-gray-800">
              {t("locationConfirmation")}
            </h4>
            <p className="text-gray-600">{t("note")}: {t("pamCodeDifferMessage")}</p>
            <p className="text-gray-600 mt-2">
              {t("newPamCodeMessage")}:{" "}
              <span className="font-semibold text-blue-600">
                {APIresponse.predictedPamCode}
              </span>
            </p>

            <div className="mt-4 flex justify-between space-x-2">
              <button
                className="px-4 py-2 bg-gray-400 text-white rounded-md hover:bg-red-700"
                onClick={() => setIsModalVisible(false)}
              >
                {t("cancel")}
              </button>
              <button
                className="px-4 py-2 text-white rounded-md bg-blue-600 hover:bg-blue-700"
                onClick={handleConfirmUpdate}
              >
                {t("confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {imageToDelete !== null && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-slate-950/60 p-4" role="dialog" aria-modal="true" aria-labelledby="delete-image-title">
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-2xl">
            <div className="flex items-start justify-between">
              <div>
                <h2 id="delete-image-title" className="text-lg font-bold text-slate-900">Delete image?</h2>
                <p className="mt-1 text-sm text-slate-500">This action cannot be undone.</p>
              </div>
              <button type="button" onClick={() => setImageToDelete(null)} disabled={isDeletingImage} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100" aria-label="Close delete image dialog"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={() => setImageToDelete(null)} disabled={isDeletingImage} className="rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700">Cancel</button>
              <button type="button" onClick={() => imageToDelete !== null && handleDeleteImage(imageToDelete)} disabled={isDeletingImage} className="inline-flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"><Trash2 className="h-4 w-4" />{isDeletingImage ? "Deleting..." : "Delete image"}</button>
            </div>
          </div>
        </div>
      )}

      {selectedImage && (
        <div
          className="fixed inset-0 z-[1100] flex items-center justify-center bg-slate-950/70 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Maximized address image"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-h-full max-w-6xl" onClick={(event) => event.stopPropagation()}>
            <img
              src={selectedImage.url}
              alt="Maximized address image"
              className="max-h-[90vh] max-w-full rounded-lg object-contain shadow-2xl"
            />
            <button
              type="button"
              onClick={() => setSelectedImage(null)}
              className="absolute right-2 top-2 rounded-full bg-black/60 p-2 text-white hover:bg-black/80 focus:outline-none focus:ring-2 focus:ring-white"
              aria-label="Close maximized image"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      )}





      <Footer />
    </div>
  );
};

export default EditAddress;
function t(key: string): string {
  throw new Error("Function not implemented.");
}